import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	cachedRecommendedDocumentsQuery,
	recommendedDocumentsQuery,
} from '@atlassian/search-client/recommended-documents';
import {
	type RecommendedDocument,
	type RecommendedDocumentEntity,
} from '@atlassian/search-client/recommended-documents/types';

/**
 * Default max number from backend is 20, we have maximum 3 slots for recommendations in pre-query,
 * but we're trying to fetch as many as possible so user would still be able to see more recommendations in post-query search results
 */
export const MAX_RECOMMENDED_DOCUMENTS = 20;

export const fetchRecommendedDocuments: (
	cloudId: string,
	userId: string,
	aggAbsoluteUrl: string | undefined,
	headers?: Record<string, string>,
) => Promise<
	{
		id: string;
		title: string;
		data: RecommendedDocument;
		type: 'recommended-document';
	}[]
> = async (
	cloudId: string,
	userId: string,
	aggAbsoluteUrl: string | undefined,
	headers?: Record<string, string>,
) => {
	const serveAutorefreshCohort =
		FeatureGates.getExperimentValue<string>(
			'quick_find_autorefresh_experiment_v3',
			'cohort',
			'control',
			{ fireExperimentExposure: false },
		) === 'autorefresh';

	const response = serveAutorefreshCohort
		? await cachedRecommendedDocumentsQuery({
				variables: {
					userId,
					tenantId: cloudId,
					caller: 'quick-find',
					maxNumberOfResults: MAX_RECOMMENDED_DOCUMENTS,
				},
				aggAbsoluteUrl,
				onlyRefreshIfExpired: true,
				returnExpired: false,
				headers,
			})
		: await recommendedDocumentsQuery({
				variables: {
					userId,
					tenantId: cloudId,
					caller: 'quick-find',
					maxNumberOfResults: MAX_RECOMMENDED_DOCUMENTS,
				},
				aggAbsoluteUrl,
				headers,
			});

	const recommendations: RecommendedDocument[] =
		response.data?.contextService?.quickfindUserRecommendations || [];
	return recommendations.filter((r) => Boolean(r.object)).map(mapEssentialData);
};

export const mapEssentialData = <T extends RecommendedDocumentEntity>(
	result: RecommendedDocument<T>,
): {
	id: string;
	title: string;
	data: RecommendedDocument<T>;
	type: 'recommended-document';
} => {
	const object = result.object;
	const title =
		object?.__typename === 'ConfluencePage'
			? object.title || ''
			: object?.__typename === 'ExternalDocument'
				? object.displayName || ''
				: '';

	return {
		id: result.id,
		title,
		data: result,
		type: 'recommended-document' as const,
	};
};
